/**
 * Gin Rummy meld logic: runs (three or more consecutive cards of one suit, ace low only), sets (three or four cards of
 * one rank), a solver that finds the arrangement of a hand with the least deadwood, and the layoff of a player's
 * deadwood onto the knocker's melds.
 *
 * Card values: ace 1, 2-10 face value, J/Q/K 10.
 */
import { parseCard, type CardId } from '../../core/cards.js';

export interface GinMeld {
  kind: 'set' | 'run';
  cards: CardId[];
}

export interface GinArrangement {
  melds: GinMeld[];
  /** Cards not in any meld. */
  deadwood: CardId[];
  /** Total value of the deadwood. */
  points: number;
}

/** Deadwood value of a card. */
export function ginCardValue(card: CardId): number {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : Math.min(r, 10);
}

/** Rank for runs: the ace is always low. */
const runRank = (card: CardId): number => {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : r;
};

export function isGinSet(cards: readonly CardId[]): boolean {
  if (cards.length < 3 || cards.length > 4) return false;
  const cs = cards.map(parseCard);
  return cs.every((c) => c.rank === cs[0]!.rank) && new Set(cs.map((c) => c.suit)).size === cs.length;
}

export function isGinRun(cards: readonly CardId[]): boolean {
  if (cards.length < 3) return false;
  const cs = cards.map(parseCard);
  if (!cs.every((c) => c.suit === cs[0]!.suit)) return false;
  const ranks = cards.map(runRank).sort((a, b) => a - b);
  return ranks.every((r, i) => i === 0 || r === ranks[i - 1]! + 1);
}

const popcount = (x: number): number => {
  let n = 0;
  while (x) {
    x &= x - 1;
    n++;
  }
  return n;
};
const ctz = (x: number): number => 31 - Math.clz32(x & -x);

/** Solver over one hand (up to 11 cards); `exclude` sets one card (the discard) aside. */
export class GinAnalysis {
  readonly cards: CardId[];
  private readonly values: number[];
  private readonly melds: { mask: number; kind: 'set' | 'run' }[] = [];
  private readonly byLow: { mask: number; kind: 'set' | 'run' }[][];
  private readonly n: number;
  private readonly full: number;
  private readonly memo: Int16Array;

  constructor(cards: readonly CardId[]) {
    if (cards.length > 12) throw new Error('GinAnalysis supports at most 12 cards');
    this.cards = [...cards];
    this.n = cards.length;
    this.full = (1 << this.n) - 1;
    this.values = cards.map(ginCardValue);
    for (let mask = 7; mask <= this.full; mask++) {
      if (popcount(mask) < 3) continue;
      const picked: CardId[] = [];
      for (let m = mask; m; m &= m - 1) picked.push(cards[ctz(m)]!);
      if (isGinSet(picked)) this.melds.push({ mask, kind: 'set' });
      else if (isGinRun(picked)) this.melds.push({ mask, kind: 'run' });
    }
    this.byLow = Array.from({ length: this.n }, () => []);
    for (const m of this.melds) this.byLow[ctz(m.mask)]!.push(m);
    this.memo = new Int16Array(1 << this.n).fill(-1);
  }

  private solve(used: number): number {
    if (used === this.full) return 0;
    const hit = this.memo[used]!;
    if (hit >= 0) return hit;
    const i = ctz(~used & this.full);
    let best = this.values[i]! + this.solve(used | (1 << i));
    for (const m of this.byLow[i]!) {
      if (m.mask & used) continue;
      const v = this.solve(used | m.mask);
      if (v < best) best = v;
    }
    this.memo[used] = best;
    return best;
  }

  /** Least possible deadwood points, with card index `exclude` (or -1) set aside. */
  deadwood(exclude = -1): number {
    return this.solve(exclude >= 0 ? 1 << exclude : 0);
  }

  /** A best arrangement (melds + leftover cards) with card index `exclude` (or -1) set aside. */
  arrangement(exclude = -1): GinArrangement {
    let used = exclude >= 0 ? 1 << exclude : 0;
    const chosen: { mask: number; kind: 'set' | 'run' }[] = [];
    while (used !== this.full) {
      const i = ctz(~used & this.full);
      const here = this.solve(used);
      const meld = this.byLow[i]!.find((m) => !(m.mask & used) && this.solve(used | m.mask) === here);
      if (meld) {
        chosen.push(meld);
        used |= meld.mask;
      } else {
        used |= 1 << i;
      }
    }
    const inMeld = new Set<number>();
    const melds: GinMeld[] = chosen.map((m) => {
      const cards: CardId[] = [];
      for (let i = 0; i < this.n; i++)
        if (m.mask & (1 << i)) {
          cards.push(this.cards[i]!);
          inMeld.add(i);
        }
      return { kind: m.kind, cards: m.kind === 'run' ? cards.sort((a, b) => runRank(a) - runRank(b)) : cards };
    });
    const deadwood = this.cards.filter((_, i) => i !== exclude && !inMeld.has(i));
    return { melds, deadwood, points: deadwood.reduce((n, c) => n + ginCardValue(c), 0) };
  }
}

export const analyzeGinHand = (cards: readonly CardId[]): GinAnalysis => new GinAnalysis(cards);

/**
 * Adds each deadwood card that extends one of the knocker's melds (a fourth card of a set, or the next card up or down
 * a run), repeating until nothing more fits. Returns the extended melds, the cards laid off and what is left.
 */
export function ginLayoff(melds: readonly GinMeld[], deadwood: readonly CardId[]): { melds: GinMeld[]; laidOff: CardId[]; remaining: CardId[] } {
  const out: GinMeld[] = melds.map((m) => ({ kind: m.kind, cards: [...m.cards] }));
  let remaining = [...deadwood];
  const laidOff: CardId[] = [];
  let changed = true;
  while (changed) {
    changed = false;
    for (const card of remaining) {
      const c = parseCard(card);
      const target = out.find((m) => {
        if (m.kind === 'set') return m.cards.length < 4 && parseCard(m.cards[0]!).rank === c.rank && !m.cards.some((x) => parseCard(x).suit === c.suit);
        if (parseCard(m.cards[0]!).suit !== c.suit) return false;
        const ranks = m.cards.map(runRank);
        const r = runRank(card);
        return r === Math.min(...ranks) - 1 || r === Math.max(...ranks) + 1;
      });
      if (!target) continue;
      target.cards.push(card);
      if (target.kind === 'run') target.cards.sort((a, b) => runRank(a) - runRank(b));
      laidOff.push(card);
      remaining = remaining.filter((x) => x !== card);
      changed = true;
      break;
    }
  }
  return { melds: out, laidOff, remaining };
}

/** The discard that leaves the least deadwood (ties: the highest-value card), never `avoid`. */
export function ginBestDiscard(hand: readonly CardId[], avoid: CardId | null = null): CardId {
  const an = new GinAnalysis(hand);
  let best = -1;
  let bestPts = Infinity;
  hand.forEach((c, i) => {
    if (c === avoid) return;
    const pts = an.deadwood(i);
    if (pts < bestPts || (pts === bestPts && ginCardValue(c) > ginCardValue(hand[best]!))) {
      bestPts = pts;
      best = i;
    }
  });
  return hand[best]!;
}
